import React, { useEffect, useMemo, useState } from 'react'
import ContentHeader from 'components/UI/ContentHeader/ContentHeader'
import { superAxiosRequest } from 'axiosApi'
import useTableFixedHeader from 'hooks/useTableFixedHeader'
import Loader from 'components/ContentBox/Global/Loader/Loader'
import THead from 'components/ContentBox/Global/ReportTable/THead'
import st from 'components/ContentBox/Global/ReportTable/Table.module.scss'
import styles from './ClientOccupancy.module.scss'
import { ExcelRowsParams } from 'components/utils/ExcelExport'
import ExportExcelButton from '../ExportExcelButton/ExportExcelButton'
import { observer } from 'mobx-react'
import useLocalization from 'hooks/useLocalization'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import { useHistory } from 'react-router-dom'
import { timeConverter } from 'components/ContentBox/Online-schedule/components/functions'

const cols = [
  { name: 'Локация' },
  { name: 'Количество студентов' },
  { name: 'Количество групп' },
  { name: 'Среднее значение' },
]

const ClientOccupancy = () => {
  const [catalog, setCatalog] = useState({})
  const {
    refWrapper,
    refSticky,
    refThead,
    data,
    setData,
    isLoading,
    setIsLoading,
  } = useTableFixedHeader()
  const dateAfter = new Date()
  dateAfter.setMonth(dateAfter.getMonth() - 1)
  const firstDayOfLastMoth = new Date(new Date(dateAfter).setDate(1))
  const lastDayOfLastMoth = new Date(new Date().setDate(0))
  const { translate } = useLocalization({
    section: 'ebooks',
    subsection: 'ebooks_books',
    dict: {
      edit: 'Edit',
      copy: 'Copy',
      delete: 'Delete',
      save: 'Save',
      filter: 'Filter',
      company: 'Company',
      reset_all: 'Reset all',
      apply: 'Apply',
      search: 'Search',
      start_date: 'Start date',
      end_date: 'End date',
    },
  })
  const history = useHistory()
  const totalStudents = data?.reduce(
    (acc, item) => acc + item.result.students,
    0,
  )
  const totalGroups = data?.reduce((acc, item) => acc + item.result.groups, 0)
  const [filter, updateFilter] = useLocalStorageFilter()
  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore

  const filterCnf = useMemo(() => {
    return [
      {
        type: 'select-multi',
        label: 'Локация',
        placeholder: 'Локация',
        values: catalog?.locations,
        sort_key: 'name',
        nameState: 'location',
      },
      {
        type: 'select-multi',
        label: 'Статус группы',
        placeholder: 'Статус группы',
        values: catalog?.group_statuses,
        nameState: 'group_status',
      },
      {
        type: 'select-multi',
        label: 'Тип группы',
        placeholder: 'Тип группы',
        nameState: 'group_type',
        values: catalog?.group_types,
      },
      {
        type: 'select-multi',
        label: 'Статус студента',
        placeholder: 'Статус студента',
        nameState: 'student_status',
        values: catalog?.student_statuses,
      },
      {
        type: 'select-multi',
        label: 'Типы программ',
        placeholder: 'Типы программ',
        nameState: 'program_types',
        values: catalog?.program_types?.items,
      },
      {
        type: 'date_day_picker',
        defaultDate: {
          after: firstDayOfLastMoth,
          before: lastDayOfLastMoth,
        },
        nameState: ['date_from', 'date_until'],
        label: 'Период',
      },
    ]
  }, [catalog])

  useEffect(() => {
    if (filter && Object.keys(filter).length > 0) {
      setIsLoading(true)
      // delete params.is_active;
      superAxiosRequest({
        method: 'get',
        link: 'progress_report/average_group_client',
        params: {
          ...filter,
          date_from: timeConverter(
            filter?.date_from ? filter?.date_from : firstDayOfLastMoth,
            'today',
          ),
          date_until: timeConverter(
            filter?.date_until ? filter?.date_until : lastDayOfLastMoth,
            'today',
          ),
        },
      })
        .then((response) => {
          setIsLoading(false)
          setData(response.data.result)
          // setOptions({ ...response.data });
        })
        .catch(() => {
          setIsLoading(false)
        })
    }
  }, [filter])

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== '/reports/occupancy-report') {
        updateFilter({})
      }
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter])

  const downloadExcel = (value) => {
    import('xlsx-js-style').then((XLSX) => {
      const merges = []
      const headRow = [
        { v: 'Локация', ...ExcelRowsParams.headerRowParam },
        { v: 'Количество студентов', ...ExcelRowsParams.headerRowParam },
        { v: 'Количество групп', ...ExcelRowsParams.headerRowParam },
        { v: 'Среднее значение', ...ExcelRowsParams.headerRowParam },
      ]
      const tableData = value.map((item) => {
        const location = catalog?.locations?.find(
          (location) => location.id === item.location,
        )
        return [
          {
            v: location?.name || '',
            ...ExcelRowsParams.tableRowParam.cell,
          },
          {
            v: item.result.students,
            ...ExcelRowsParams.tableRowParam.cell,
          },
          {
            v: item.result.groups,
            ...ExcelRowsParams.tableRowParam.cell,
          },
          {
            v: item.result.average.toFixed(2),
            ...ExcelRowsParams.tableRowParam.cell,
          },
        ]
      })

      const footerRow = [
        {
          v: `ИТОГО`,
          ...ExcelRowsParams.footerRowParams.firstCell,
        },
        {
          v: totalStudents,
          ...ExcelRowsParams.footerRowParams.cell,
        },
        {
          v: totalGroups,
          ...ExcelRowsParams.footerRowParams.cell,
        },
        {
          v: (totalStudents / totalGroups).toFixed(2),
          ...ExcelRowsParams.footerRowParams.cell,
        },
      ]

      const wscols = [{ wch: 30 }, { wch: 30 }, { wch: 30 }, { wch: 30 }]
      const rows = value.map(() => ({ hpx: 30 })).concat({ hpx: 30 })

      let wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet([headRow, ...tableData, footerRow])
      ws['!cols'] = wscols
      ws['!merges'] = merges
      ws['!rows'] = rows
      XLSX.utils.book_append_sheet(wb, ws, 'List 1')

      XLSX.writeFile(wb, 'Наполняемость клиентов.xlsx')
    })
  }

  useEffect(() => {
    setIsLoading(true)
    superAxiosRequest({
      method: 'get',
      link: 'progress_report/average_group_client',
    })
      .then(({ data }) => {
        setIsLoading(false)
        setCatalog(data)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }, [setIsLoading])

  return (
    <>
      <ContentHeader title='Наполняемость клиентов' />
      <div className={styles.filterContainer}>
        <SearchBooks
          onSearch={(text) => {
            updateFilter({ ...filter, search: text })
            changeTextValue('search', {
              key: 'search',
              label: 'Search',
              value: text,
            })
          }}
          onClear={() => {
            const { search, ...obj } = filter
            updateFilter(obj)
            setTextValues(
              filterTextValues.filter((field) => field.key !== 'search'),
            )
          }}
          translate={translate}
          placeholder={`Search...`}
          value={filter?.search || ''}
        />
        {!!filterTextValues.length && (
          <FilterTextValues
            onDelete={(key) => {
              const { [key]: _, ...obj } = filter
              updateFilter(obj)
            }}
          />
        )}
        {isOpenFilter && (
          <AsideFilter
            translate={translate}
            getFilterParams={updateFilter}
            config={filterCnf}
            filterValue={filter}
            onDropFilter={() => {
              updateFilter({})
              closeFilter()
            }}
          />
        )}
      </div>
      {isLoading ? (
        <Loader />
      ) : Array.isArray(data) &&
        data.map((el) => el.dates).flat().length === 0 ? (
        <h1 className={st.noData}>Нет данных для отображения!</h1>
      ) : (
        <div>
          <div className={styles.excelButton}>
            {data && (
              <ExportExcelButton data={data} downloadExcel={downloadExcel} />
            )}
          </div>
          <div className={st.tableWrapper} ref={refWrapper}>
            <div ref={refSticky} className={st.sticky}>
              <table className={st.table}>
                <THead cols={cols} />
              </table>
            </div>
            <table className={`${st.table} ${st.tbodyStriped}`}>
              <THead cols={cols} ref={refThead} />
              <tbody className={st.tbody}>
                {Array.isArray(data) &&
                  data.map((item, index) => {
                    const location = catalog?.locations?.find(
                      (location) => location.id === item.location,
                    )

                    return (
                      <tr className={`${st.bdt} ${st.thead}`} key={index}>
                        <td className={styles.item}>{location?.name}</td>
                        <td className={styles.item}>{item.result.students}</td>
                        <td className={styles.item}>{item.result.groups}</td>
                        <td className={styles.item}>
                          {item.result.average.toFixed(2)}
                        </td>
                      </tr>
                    )
                  })}
                <tr>
                  <td className={st.tdTotal}>Итого</td>
                  <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                    {totalStudents}
                  </td>
                  <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                    {totalGroups}
                  </td>
                  <td className={`${st.tdTotal} ${st.textAlignCenter}`}>
                    {(totalStudents / totalGroups).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  )
}

export default observer(ClientOccupancy)
